import { FC } from "react";
import NcImage from "shared/NcImage/NcImage";
import { MapPinIcon, CalendarIcon, UsersIcon, ClockIcon, CurrencyEuroIcon } from "@heroicons/react/24/outline";

interface ReservationDetailsProps {
  location: {
    title?: string;
    city?: string;
    region?: string | null;
    surface?: string;
    images?: string[];
  };
  reservation: {
    selectedDate?: string;
    startHour?: string;
    endHour?: string;
    visitor?: number;
    price?: number;
  };
}

const ReservationDetails: FC<ReservationDetailsProps> = ({ location, reservation }) => {
  const formattedDate = new Date(reservation.selectedDate || "").toLocaleDateString("fr-FR");

  return (
    <div className="space-y-8">
      {/* Location Info Card */}
      <div className="bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-all duration-300 card-hover-lift">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-shrink-0 w-full lg:w-48">
            <div className="relative overflow-hidden rounded-xl shadow-lg group">
              <div className="aspect-w-16 aspect-h-10">
                <NcImage
                  className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                  src={location.images?.[0] ? process.env.REACT_APP_CLOUD_FRONT + location.images[0] : ""}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </div>

          <div className="flex-1 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <MapPinIcon className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {location.city}, {location.region}
                </span>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{location.title}</h3>
            </div>

            {location.surface && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                  />
                </svg>
                {location.surface} m²
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 group">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <CalendarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Date</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{formattedDate}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 group">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <UsersIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Invités</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{reservation.visitor} Personnes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <svg
            className="w-6 h-6 text-blue-600 dark:text-blue-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Récapitulatif de réservation
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Date</span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">{formattedDate}</span>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <ClockIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Horaires</span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">
              {reservation.startHour} - {reservation.endHour}
            </span>
          </div>

          <div className="flex items-center justify-between py-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                <CurrencyEuroIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <span className="font-bold text-green-700 dark:text-green-300 text-lg">Total</span>
            </div>
            <span className="font-bold text-2xl text-green-600 dark:text-green-400">{reservation.price}€</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationDetails;
